import type { CubeQueryRecord, ExtractorState, HttpMethod } from '@/dev/types/cubeQueryTypes';
import { isCubeQuery } from '@/dev/utils/cubeQueryGuards';

const QUERIES_KEY = 'cube_query_extractor_queries';

export interface StoredState {
  isRecording: boolean;
  queries: CubeQueryRecord[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(memberName => typeof memberName === 'string');

const isHttpMethod = (value: unknown): value is HttpMethod =>
  value === 'CUBE_LOAD' || value === 'GET' || value === 'POST';

const isCubeQueryRecord = (value: unknown): value is CubeQueryRecord => {
  if (!isRecord(value)) return false;

  return (
    typeof value.id === 'string' &&
    typeof value.timestamp === 'number' &&
    typeof value.componentName === 'string' &&
    typeof value.pagePath === 'string' &&
    isCubeQuery(value.query) &&
    isStringArray(value.queryKey) &&
    typeof value.url === 'string' &&
    isHttpMethod(value.method) &&
    (value.duration === undefined || typeof value.duration === 'number') &&
    (value.rowCount === undefined || typeof value.rowCount === 'number') &&
    (value.status === undefined || typeof value.status === 'number') &&
    (value.error === undefined || typeof value.error === 'string')
  );
};

export const loadState = (): StoredState => {
  try {
    const savedQueries = localStorage.getItem(QUERIES_KEY);
    const parsedQueries: unknown = savedQueries ? JSON.parse(savedQueries) : [];
    const queries = Array.isArray(parsedQueries) ? parsedQueries.filter(isCubeQueryRecord) : [];

    // Recording depends on a live runtime subscription that cannot survive a
    // page reload, so it must never be restored from storage — otherwise the UI
    // would claim to be recording while nothing is actually intercepted.
    return { isRecording: false, queries };
  } catch {
    return { isRecording: false, queries: [] };
  }
};

export const saveState = (state: ExtractorState): void => {
  try {
    localStorage.setItem(QUERIES_KEY, JSON.stringify(state.queries));
  } catch {}
};
